import type { ReactNode } from 'react';

import { formatMoney } from '@/shared/lib/format';

import { ehDinheiro, rotuloDe } from './valores';

/**
 * Valor de parametro para leitura: o da versao ativa, o de uma versao antiga, o de um
 * lado da comparacao.
 *
 * Dinheiro sai em reais pelo formatador comum, nulo sai "Em branco" e nunca zero, e
 * os valores de dominio saem pelo nome legivel do catalogo.
 */
export function ValorLegivel({
  valor,
  rotulos,
  unidade,
}: {
  valor: unknown;
  rotulos: Record<string, string>;
  /** Unidade do parametro simples, como "meses". */
  unidade?: string | undefined;
}) {
  return <>{formatar(valor, rotulos, unidade, 0)}</>;
}

const vazio = 'text-ink-500 italic';

function formatar(
  valor: unknown,
  rotulos: Record<string, string>,
  unidade: string | undefined,
  nivel: number,
): ReactNode {
  if (valor === null || valor === undefined) {
    return <span className={vazio}>Em branco</span>;
  }
  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Nao';
  if (typeof valor === 'number') {
    const texto = valor.toLocaleString('pt-BR');
    return unidade && nivel === 0 ? `${texto} ${unidade}` : texto;
  }
  if (typeof valor === 'string') {
    return valor === '' ? (
      <span className={vazio}>Nao definido</span>
    ) : (
      rotuloDe(valor, rotulos)
    );
  }
  if (ehDinheiro(valor)) return formatMoney(valor);
  if (Array.isArray(valor)) return lista(valor, rotulos, nivel);
  if (typeof valor === 'object')
    return mapa(valor as Record<string, unknown>, rotulos, nivel);
  // Fora do JSON (funcao, simbolo): nao ha o que mostrar.
  return null;
}

function lista(
  itens: unknown[],
  rotulos: Record<string, string>,
  nivel: number,
): ReactNode {
  if (itens.length === 0) return <span className={vazio}>Nenhum</span>;
  const linhas = itens.every(
    (item) =>
      item !== null &&
      typeof item === 'object' &&
      !Array.isArray(item) &&
      !ehDinheiro(item),
  );
  if (linhas) {
    const registros = itens as Record<string, unknown>[];
    const colunas = [...new Set(registros.flatMap((linha) => Object.keys(linha)))];
    return (
      <table className="text-xs">
        <thead>
          <tr>
            {colunas.map((coluna) => (
              <th key={coluna} className="pr-3 text-left font-semibold text-ink-600">
                {rotuloDe(coluna, rotulos)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {registros.map((linha, indice) => (
            <tr key={indice}>
              {colunas.map((coluna) => (
                <td key={coluna} className="pr-3 align-top">
                  {formatar(linha[coluna], rotulos, undefined, nivel + 1)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return (
    <span>
      {itens.map((item, indice) => (
        <span key={indice}>
          {indice > 0 ? ', ' : null}
          {formatar(item, rotulos, undefined, nivel + 1)}
        </span>
      ))}
    </span>
  );
}

function mapa(
  objeto: Record<string, unknown>,
  rotulos: Record<string, string>,
  nivel: number,
): ReactNode {
  const chaves = Object.keys(objeto);
  if (chaves.length === 0) return <span className={vazio}>Nenhum</span>;

  // Mapa de mapas de sim ou nao: matriz, como a tabela de grupos elegiveis por
  // classe.
  const matriz = chaves.every((chave) => {
    const linha = objeto[chave];
    return (
      linha !== null &&
      typeof linha === 'object' &&
      !Array.isArray(linha) &&
      Object.values(linha).every((celula) => typeof celula === 'boolean')
    );
  });
  if (matriz) {
    const colunas = [
      ...new Set(chaves.flatMap((chave) => Object.keys(objeto[chave] as object))),
    ];
    return (
      <table className="text-xs">
        <thead>
          <tr>
            <th />
            {colunas.map((coluna) => (
              <th key={coluna} className="px-1.5 text-left font-semibold text-ink-600">
                {rotuloDe(coluna, rotulos)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {chaves.map((chave) => {
            const linha = objeto[chave] as Record<string, boolean>;
            return (
              <tr key={chave}>
                <th className="pr-2 text-left font-semibold text-ink-600">
                  {rotuloDe(chave, rotulos)}
                </th>
                {colunas.map((coluna) => (
                  <td key={coluna} className="px-1.5">
                    {linha[coluna] === undefined ? '' : linha[coluna] ? 'Sim' : 'Nao'}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  const grande = chaves.length > LINHAS_DO_MAPA;
  const visiveis = grande ? chaves.slice(0, LINHAS_DO_MAPA) : chaves;
  return (
    <>
      {grande ? (
        <p className="mb-1 text-xs text-ink-600">
          {`${chaves.length.toLocaleString('pt-BR')} linhas: ${resumoDosValores(objeto, rotulos)}. Mostrando as ${LINHAS_DO_MAPA} primeiras.`}
        </p>
      ) : null}
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
        {visiveis.map((chave) => (
          <div key={chave} className="contents">
            <dt className="text-ink-600">{rotuloDe(chave, rotulos)}</dt>
            <dd className="text-ink-900">
              {formatar(objeto[chave], rotulos, undefined, nivel + 1)}
            </dd>
          </div>
        ))}
      </dl>
    </>
  );
}

/** Quantas linhas um mapa mostra na leitura. A classificacao dos CNAEs tem mais de mil. */
const LINHAS_DO_MAPA = 30;

/** Quantas linhas ha de cada valor, como "Aceito 586, Negado 393". */
function resumoDosValores(
  objeto: Record<string, unknown>,
  rotulos: Record<string, string>,
): string {
  const contagem = new Map<string, number>();
  for (const valor of Object.values(objeto)) {
    const nome = typeof valor === 'string' ? rotuloDe(valor, rotulos) : 'outros';
    contagem.set(nome, (contagem.get(nome) ?? 0) + 1);
  }
  return [...contagem.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([nome, quantidade]) => `${nome} ${quantidade.toLocaleString('pt-BR')}`)
    .join(', ');
}
